// Phase 1.5 item #2C · Evals pages — the "Run eval" execution UX, restyled to
// the Eval-Execution Engine mockup (docs/design/eval-execution-engine-mockup-v1.html,
// Screens 1 · Kickoff / 2 · Running / 3 · Results). Presentation renders
// entirely through EvalUI.jsx; every effect, poll loop, API call, and
// format-helper contract is unchanged (logic lives in the 100%-covered
// evalRunFormat.js unit; the abort behaviour is locked by EvalsPages.abort.test.jsx).
//
// Evals = dataset runs:
//   • Kickoff (EvalsListPage): pick a dataset + an agent-under-test, click
//     "Run eval" → POST a scored run → jump to its detail route, with the
//     "Recent runs" table below.
//   • Run detail (EvalRunDetailPage): while in-flight, poll every 5s and render
//     the progress bar + counters + streaming per-item rows (Screen 2). On
//     completion render the aggregate hero (accuracy donut + breakdown chips +
//     basis + latency + cost) + per-item table with verdicts, scorer, latency,
//     and Langfuse trace deep-links, and expandable expected-vs-actual for
//     non-passing rows (Screen 3).
import React, { useEffect, useRef, useState } from "react";
import { COLORS } from "../../../theme/colors";
import { observabilityApi, agentApi } from "../../../api/client";
import { EmptyState, ErrorState, LoadingRow } from "./SharedUI";
import { humanizeError as _humanizeError } from "../../../utils/humanizeError";
import {
  SectionHead, Panel, Field, selectStyle, Btn, Callout, B, TableCard, THead, Tr, Td,
  QCell, StatusPill, VerdictBadge, ScorerPill, TraceLink, BackButton, ProgressPanel, Num,
  Counters, Counter, Hero, DiffRow, Legend, Spinner,
} from "./EvalUI";
import {
  normalizeRunItems,
  buildRunDetailId,
  parseRunDetailId,
  isRunInFlight,
  accuracyBasis,
  accuracyPct,
  progressPct,
  verdictOfItem,
  runOutcomeChips,
  scoringMethodOf,
  indexDatasetItems,
  inspectorPathForItem,
  questionForItem,
  latencyOf,
  formatLatency,
  unwrapPage,
  clampOffset,
  pageLabel,
  hasPrevPage,
  hasNextPage,
  filterAgents,
  nextHighlight,
  expectedOutputForItem,
  actualOutputForItem,
} from "./evalRunFormat";

const PAGE_SIZE = 25;

// ─── Pager — Prev / Next + "showing X–Y of TOTAL" (shared by both screens) ──
// Coverage-excluded shell; all math lives in evalRunFormat (pageLabel /
// hasPrevPage / hasNextPage), unit-tested there.
function Pager({ offset, limit, total, onPrev, onNext, busy }) {
  if (!Number.isFinite(Number(total)) || Number(total) <= limit) return null;
  const prevOk = hasPrevPage(offset) && !busy;
  const nextOk = hasNextPage(offset, limit, total) && !busy;
  const btn = (label, ok, onClick, key) => (
    <button
      key={key}
      onClick={ok ? onClick : undefined}
      disabled={!ok}
      style={{
        font: "inherit", fontSize: 12, fontWeight: 600,
        color: ok ? COLORS.textMuted : COLORS.textDim,
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 6, padding: "5px 12px", cursor: ok ? "pointer" : "default",
        opacity: ok ? 1 : 0.55,
      }}
    >{label}</button>
  );
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "flex-end",
      gap: 10, marginTop: 10, fontSize: 11.5, color: COLORS.textDim,
    }}>
      <span style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
        {pageLabel(offset, limit, total)}
      </span>
      {btn("‹ Prev", prevOk, onPrev, "p")}
      {btn("Next ›", nextOk, onNext, "n")}
    </div>
  );
}

// ─── AgentCombobox — type-to-filter agent picker (Screen 1 kickoff) ──
// Replaces the plain <select> when there are many agents/supervisors.
// Coverage-excluded shell; the filter (filterAgents) and keyboard-highlight
// math (nextHighlight) are unit-tested in evalRunFormat.
function AgentCombobox({ agents, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(-1);
  const rootRef = useRef(null);
  const blurTimer = useRef(null);

  const selected = agents.find((a) => a && a.id === value) || null;
  const selectedLabel = selected ? (selected.name || selected.id) : "";
  const filtered = filterAgents(agents, open ? query : "");

  const close = () => { setOpen(false); setQuery(""); setHighlight(-1); };
  const commit = (a) => { if (a) onChange(a.id); close(); };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlight((h) => nextHighlight(h, e.key === "ArrowDown" ? 1 : -1, filtered.length));
    } else if (e.key === "Enter") {
      if (open && highlight >= 0 && filtered[highlight]) { e.preventDefault(); commit(filtered[highlight]); }
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); close(); }
    }
  };

  const listboxId = "eval-agent-listbox";
  return (
    <div
      ref={rootRef}
      style={{ position: "relative", minWidth: 210 }}
      onBlur={() => { blurTimer.current = setTimeout(close, 120); }}
      onFocus={() => { if (blurTimer.current) clearTimeout(blurTimer.current); }}
    >
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={open && highlight >= 0 ? `eval-agent-opt-${highlight}` : undefined}
        disabled={disabled}
        placeholder={agents.length === 0 ? "No agents" : "Search agents…"}
        value={open ? query : selectedLabel}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(-1); }}
        onFocus={() => !disabled && setOpen(true)}
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={onKeyDown}
        style={{ ...selectStyle(), width: "100%", cursor: disabled ? "default" : "text",
          paddingRight: 11 }}
      />
      {open && !disabled && (
        <ul
          id={listboxId}
          role="listbox"
          style={{
            position: "absolute", zIndex: 30, top: "calc(100% + 4px)", left: 0, right: 0,
            margin: 0, padding: 4, listStyle: "none", maxHeight: 260, overflowY: "auto",
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          }}
        >
          {filtered.length === 0 ? (
            <li style={{ padding: "8px 10px", fontSize: 12, color: COLORS.textDim }}>
              No matching agents
            </li>
          ) : filtered.map((a, i) => {
            const active = i === highlight;
            const isSel = a.id === value;
            return (
              <li
                key={a.id}
                id={`eval-agent-opt-${i}`}
                role="option"
                aria-selected={isSel}
                onMouseDown={(e) => { e.preventDefault(); commit(a); }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding: "7px 10px", fontSize: 13, borderRadius: 4, cursor: "pointer",
                  color: COLORS.text, fontWeight: isSel ? 700 : 500,
                  background: active ? (COLORS.surfaceHover || "rgba(124,58,237,0.12)") : "transparent",
                }}
              >
                {a.name || a.id}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// MNT-014: shared humanizer + this page's status-specific overrides.
const humanizeError = (e) => _humanizeError(e, { 500: "Langfuse query failed." });

const unwrapList = (res) =>
  Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

// ─── Kickoff (Screen 1) — dataset + agent select, "Run eval", recent runs ──

export function EvalsListPage({ navigate }) {
  const [datasets, setDatasets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [runs, setRuns] = useState(null);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsOffset, setRunsOffset] = useState(0);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  // Load datasets + agents once. Agents-under-test are the tenant's
  // real/regular agents (ephemeral sub-agents are excluded).
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const [dsRes, agRes] = await Promise.all([
          observabilityApi.listDatasets(),
          agentApi.list(),
        ]);
        if (controller.signal.aborted) return;
        const ds = unwrapList(dsRes);
        setDatasets(ds);
        if (ds[0]) setSelectedDatasetId((prev) => prev || ds[0].id);
        const ag = unwrapList(agRes).filter((a) => a && !a.metadata_json?.ephemeral);
        setAgents(ag);
        if (ag[0]) setSelectedAgentId((prev) => prev || ag[0].id);
      } catch (e) {
        if (controller.signal.aborted || e?.name === "AbortError") return;
        setError(humanizeError(e));
      }
    })();
    return () => controller.abort();
  }, []);

  const loadRuns = async (dsId, offset = 0, signal) => {
    setError(null);
    try {
      const res = await observabilityApi.listEvalRuns(dsId, {
        limit: PAGE_SIZE, offset,
        ...(signal ? { signal } : {}),
      });
      if (signal?.aborted) return;
      if (res?.success === false) {
        setError(res.error || "Failed to load runs");
        setRuns([]);
        setRunsTotal(0);
        return;
      }
      // Page through with pagination.total so the count never silently caps.
      const { items, total } = unwrapPage(res, PAGE_SIZE);
      setRuns(items);
      setRunsTotal(total);
      setRunsOffset(offset);
    } catch (e) {
      if (signal?.aborted || e?.name === "AbortError") return;
      setError(humanizeError(e));
      setRuns([]);
      setRunsTotal(0);
    }
  };

  // Reload the recent-runs table whenever the selected dataset changes — reset
  // to the first page.
  useEffect(() => {
    if (!selectedDatasetId) {
      setRuns([]);
      setRunsTotal(0);
      setRunsOffset(0);
      return undefined;
    }
    const controller = new AbortController();
    loadRuns(selectedDatasetId, 0, controller.signal);
    return () => controller.abort();
  }, [selectedDatasetId]);

  // E3 · run-from-UI. Starts a scored run of the selected agent over the
  // selected dataset, then navigates into the run-detail (poll) view.
  const runEval = async () => {
    if (!selectedDatasetId || !selectedAgentId) return;
    setRunError(null);
    setRunning(true);
    try {
      const res = await observabilityApi.createDatasetRun(selectedDatasetId, selectedAgentId);
      if (res?.success === false) {
        setRunError(res.error || "Failed to start run");
        return;
      }
      const data = res?.data || res || {};
      if (data.run_id) {
        navigate(
          `/analytics/observability/evals/${buildRunDetailId(
            selectedDatasetId, data.run_id, data.run_name,
          )}`,
        );
        return;
      }
      await loadRuns(selectedDatasetId);
    } catch (e) {
      setRunError(humanizeError(e));
    } finally {
      setRunning(false);
    }
  };

  const agentName = (run) =>
    run.agent_name
    || (agents.find((a) => a.id === run.agent_id)?.name)
    || run.agent_id
    || "—";

  const datasetName =
    datasets.find((d) => d.id === selectedDatasetId)?.name || selectedDatasetId || "—";

  const runControl = (
    <Panel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" }}>
        <Field label="Dataset">
          <select
            value={selectedDatasetId}
            onChange={(e) => setSelectedDatasetId(e.target.value)}
            disabled={running || datasets.length === 0}
            style={selectStyle()}
          >
            {datasets.length === 0 && <option value="">No datasets</option>}
            {datasets.map((d) => (
              <option key={d.id} value={d.id}>{d.name || d.id}</option>
            ))}
          </select>
        </Field>
        <Field label="Agent under test">
          <AgentCombobox
            agents={agents}
            value={selectedAgentId}
            onChange={setSelectedAgentId}
            disabled={running || agents.length === 0}
          />
        </Field>
        <Field label="Judge model" hint="BYOK — resolved from your rankings. Numeric answers scored deterministically; the judge rules the rest.">
          <div style={{ ...selectStyle(), display: "flex", alignItems: "center", cursor: "default", color: COLORS.textMuted }}>
            Tenant default (summarization)
          </div>
        </Field>
        <Btn onClick={runEval} disabled={running || !selectedDatasetId || !selectedAgentId}>
          {running ? <><Spinner size={12} /> Starting run…</> : "▶ Run eval"}
        </Btn>
        {runError && <span style={{ color: COLORS.pink, fontSize: 11 }}>{runError}</span>}
      </div>
    </Panel>
  );

  if (error && runs === null) {
    return (
      <div>
        <SectionHead title="Evals">Run a dataset against an agent — every item is executed, its answer scored against the ground truth, and the result saved as a run you can drill into.</SectionHead>
        {runControl}
        <ErrorState message={error} onRetry={() => loadRuns(selectedDatasetId)} />
      </div>
    );
  }

  const body = runs === null ? (
    <LoadingRow label="Loading eval runs…" />
  ) : runs.length === 0 ? (
    <EmptyState
      title="No eval runs yet"
      detail="Pick a dataset + an agent above and click “Run eval”. Every item is executed and scored against its ground truth."
    />
  ) : (
    <>
      <TableCard title="Recent runs" meta={pageLabel(runsOffset, PAGE_SIZE, runsTotal)} minWidth={720}>
        <THead cols={["Run", "Dataset", "Agent", "Accuracy", "Status", "Started"]} />
        <tbody>
          {runs.map((r) => {
            const detailId = buildRunDetailId(selectedDatasetId, r.run_id, r.run_name);
            return (
              <Tr key={r.run_id || r.run_name} onClick={() => navigate(`/analytics/observability/evals/${detailId}`)}>
                <Td mono style={{ color: COLORS.text }}>{String(r.run_name || r.run_id || "").slice(0, 24)}</Td>
                <Td>{datasetName}</Td>
                <Td>{agentName(r)}</Td>
                <Td mono style={{ color: COLORS.accentText, fontWeight: 700 }}>{accuracyPct(r)}</Td>
                <Td><StatusPill state={r.status} /></Td>
                <Td mono>{r.started_at ? new Date(r.started_at).toLocaleString() : "—"}</Td>
              </Tr>
            );
          })}
        </tbody>
      </TableCard>
      <Pager
        offset={runsOffset}
        limit={PAGE_SIZE}
        total={runsTotal}
        onPrev={() => loadRuns(selectedDatasetId, Math.max(0, runsOffset - PAGE_SIZE))}
        onNext={() => loadRuns(selectedDatasetId, runsOffset + PAGE_SIZE)}
      />
    </>
  );

  return (
    <div>
      <SectionHead title="Evals">Run a dataset against an agent — every item is executed, its answer scored against the ground truth, and the result saved as a run you can drill into.</SectionHead>
      {runControl}
      {error && runs !== null && (
        <div style={{ color: COLORS.pink, fontSize: 11, marginBottom: 8 }}>{error}</div>
      )}
      {body}
      <Callout icon="💡">
        <B>What "Run eval" does:</B> spawns a durable run that executes the agent over every item,
        scores each answer (numeric-tolerance + BYOK LLM-judge), writes a verdict onto each item's
        Langfuse trace, and aggregates real pass/fail/accuracy. The run keeps going if one item
        errors — that item is marked <B>ERROR</B> and excluded from the accuracy denominator.
      </Callout>
    </div>
  );
}

// ─── Run detail (Screens 2 + 3) — poll → progress → results ──────────

export function EvalRunDetailPage({ detailId, navigate }) {
  const { datasetId, runId, runName } = parseRunDetailId(detailId);

  const [status, setStatus] = useState(null);
  const [items, setItems] = useState(null);
  // total_items for the whole run — from run-items pagination, so the count
  // shown equals the run's total and never silently caps (UI-1).
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsOffset, setItemsOffset] = useState(0);
  const [datasetItems, setDatasetItems] = useState([]);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchStatus = async (signal) => {
    if (!datasetId || !runId) return null;
    const res = await observabilityApi.getDatasetRunStatus(
      datasetId, runId, signal ? { signal } : {},
    );
    if (res?.success === false) throw new Error(res.error || "Failed to load run status");
    return res?.data || res || null;
  };

  // Fetch ONE PAGE of run-items (UI-4) — the backend only trace-GETs the
  // requested page, so opening a large completed run is fast. Returns
  // { items, total } via pagination.total.
  const fetchItemsPage = async (offset, signal) => {
    if (!datasetId || !runName) return { items: [], total: 0 };
    const res = await observabilityApi.listDatasetRunItems(datasetId, runName, {
      limit: PAGE_SIZE, offset,
      ...(signal ? { signal } : {}),
    });
    if (res?.success === false) return { items: [], total: 0 };
    const { items: pageItems, total } = unwrapPage(res, PAGE_SIZE);
    return { items: pageItems, total };
  };

  // Load status + the current page of items + the golden dataset items (for
  // the per-row EXPECTED answer + question text). Reused by the effect below
  // and by the pager. `offset` is the run-items page.
  const load = async (offset, signal) => {
    setError(null);
    try {
      const st = await fetchStatus(signal);
      if (signal?.aborted) return;
      setStatus(st);
      const { items: pageItems, total } = await fetchItemsPage(offset, signal);
      if (signal?.aborted) return;
      setItems(pageItems);
      setItemsTotal(total);
      setItemsOffset(offset);
      if (datasetId) {
        // Golden set = input + expected only (cheap; not the slow trace path).
        // Loaded once so every row can show EXPECTED, regardless of paging.
        const dres = await observabilityApi.listDatasetItems(
          datasetId, { signal },
        );
        if (signal?.aborted) return;
        setDatasetItems(normalizeRunItems(dres));
      }
    } catch (e) {
      if (signal?.aborted || e?.name === "AbortError") return;
      setError(humanizeError(e));
      setItems([]);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    load(0, controller.signal);
    return () => controller.abort();
  }, [detailId]);

  // Poll every 5s while the run is in-flight — refresh status + the current
  // page (its items fill in / update their verdicts as they score).
  useEffect(() => {
    if (!status || !isRunInFlight(status.status)) return undefined;
    const controller = new AbortController();
    const tick = async () => {
      try {
        const st = await fetchStatus(controller.signal);
        if (controller.signal.aborted || !st) return;
        setStatus(st);
        const { items: pageItems, total } = await fetchItemsPage(itemsOffset, controller.signal);
        if (controller.signal.aborted) return;
        setItems(pageItems);
        setItemsTotal(total);
      } catch {
        // Transient poll error — keep last known state, retry next tick.
      }
    };
    const interval = setInterval(tick, 5000);
    return () => { controller.abort(); clearInterval(interval); };
  }, [status?.status, detailId, itemsOffset]);

  // Bounded refetch AFTER the run reaches a terminal status. Langfuse ingests
  // scores asynchronously into each trace's embedded `scores` array, so a
  // verdict/latency written in the run's final moments can still be
  // materializing when the in-flight poll stops. Without this, those rows
  // stayed stuck on verdict=pending / latency=— forever (the poll above only
  // runs WHILE in-flight). Refetch the current page a few times, then stop.
  useEffect(() => {
    if (!status || isRunInFlight(status.status)) return undefined;
    const controller = new AbortController();
    let attempts = 0;
    const MAX_ATTEMPTS = 5;
    const tick = async () => {
      attempts += 1;
      try {
        const { items: pageItems, total } = await fetchItemsPage(itemsOffset, controller.signal);
        if (controller.signal.aborted) return;
        setItems(pageItems);
        setItemsTotal(total);
      } catch {
        // Transient — a later attempt (or none) will settle it.
      }
      if (attempts >= MAX_ATTEMPTS) clearInterval(interval);
    };
    const interval = setInterval(tick, 4000);
    return () => { controller.abort(); clearInterval(interval); };
  }, [status?.status, detailId, itemsOffset]);

  // Jump to another page of run-items (pager Prev/Next).
  const goToPage = (offset) => {
    const controller = new AbortController();
    load(clampOffset(offset, PAGE_SIZE, itemsTotal), controller.signal);
  };

  const back = <BackButton label="Evals" onClick={() => navigate("/analytics/observability/evals")} />;

  if (status === null && items === null && !error) {
    return <div>{back}<LoadingRow label="Loading run…" /></div>;
  }
  if (error) {
    return <div>{back}<ErrorState message={error} onRetry={() => navigate(`/analytics/observability/evals/${detailId}`)} /></div>;
  }
  if (!datasetId || !runId) {
    return (
      <div>
        {back}
        <EmptyState
          title="Run detail unavailable"
          detail="This run link predates the run-execution engine. Open the run from the Evals list to poll its live status."
        />
      </div>
    );
  }

  const st = status || {};
  const inFlight = isRunInFlight(st.status);
  const chips = runOutcomeChips(st);
  // The current page of run-items IS the display list (UI-4). The backend
  // returns every item (incl. errored) with pagination.total = total_items,
  // so the count is correct without merging the full golden set client-side.
  const list = Array.isArray(items) ? items : [];
  // Golden items indexed by id → per-row question + EXPECTED answer (UI-3).
  const itemsById = indexDatasetItems(datasetItems);

  const datasetName = datasetItems[0]?.dataset_name || datasetId;
  const subtitle = `${st.dataset_name || ""}${st.agent_name ? ` → ${st.agent_name}` : ""}`.trim() || datasetName;

  // ── Progress (Screen 2) ──
  const progressBlock = inFlight && (
    <ProgressPanel
      pct={progressPct(st)}
      title={String(runName || runId || "").slice(0, 40)}
      subtitle={subtitle}
      statusChip={<StatusPill state={st.status} />}
      line={
        <>
          <span>Executing item <Num>{st.completed_items || 0}</Num> of <Num>{st.total_items || 0}</Num></span>
          <span><Num>{progressPct(st)}%</Num></span>
        </>
      }
    />
  );

  const counters = inFlight && (
    <Counters>
      {chips.map((c) => (
        <Counter key={c.key} label={c.label} value={c.count} color={c.color} swatch={c.color} />
      ))}
      <Counter label="Live accuracy" value={accuracyPct(st)} />
    </Counters>
  );

  // ── Aggregate hero (Screen 3) ──
  const denom = Math.max(0, (Number(st.total_items) || 0) - (Number(st.errored) || 0));
  const frac = denom > 0 ? (Number(st.passed) || 0) / denom : 0;
  const avgLatency = Number.isFinite(Number(st.average_latency_ms))
    ? `${(Number(st.average_latency_ms) / 1000).toFixed(1)}s` : "—";
  // obs-completion F7: the "Total cost" tile is removed. cost_usd is read from an
  // LLM usage.cost_usd field that no provider ever produces, so it rendered a
  // permanent, misleading $0.00 — and tenant-facing $ cost UI is disallowed
  // (invariant #1). Latency/token signals stay; dollars do not.

  const heroBlock = !inFlight && (
    <Hero
      pct={accuracyPct(st)}
      frac={frac}
      title={String(runName || runId || "").slice(0, 40)}
      subtitle={subtitle}
      statusChip={<StatusPill state={st.status} />}
      chips={chips}
      stats={[
        ["Items", st.total_items || 0],
        ["Accuracy basis", accuracyBasis(st)],
        ["Avg latency", avgLatency],
      ]}
    />
  );

  // ── Per-item table (both screens) ──
  const cols = inFlight
    ? ["#", "Question", "Verdict", "Latency", "Trace"]
    : ["#", "Question", "Verdict", "Scored by", "Latency", "Trace"];
  const colCount = cols.length;

  const itemsTable = (
    <TableCard
      title={inFlight ? "Items — streaming as they score" : "Per-item results"}
      meta={inFlight ? "polls every 5s" : pageLabel(itemsOffset, PAGE_SIZE, itemsTotal)}
      minWidth={inFlight ? 640 : 760}
    >
      <THead cols={cols} />
      <tbody>
        {list.length === 0 ? (
          <tr>
            <Td colSpan={colCount} style={{ color: COLORS.textDim }}>
              {inFlight ? "Waiting for the first item to score…" : "No items in this run."}
            </Td>
          </tr>
        ) : list.map((it, idx) => {
          const verdict = verdictOfItem(it);
          const question = questionForItem(it, itemsById);
          const method = scoringMethodOf(it);
          const dim = /timeout|error/i.test(method);
          const rowId = it.id || it.trace_id || it.dataset_item_id || idx;
          // UI-3: show expected-vs-actual for EVERY scored row (accurate too),
          // not only non-passing ones.
          const canExpand = !inFlight && !!verdict;
          const expanded = expandedId === rowId;
          const reasoning = (it.scores || []).find((s) => s && s.name === "eval_verdict")?.comment;
          const expected = expectedOutputForItem(it, itemsById);
          const actual = actualOutputForItem(it);
          // The Inspector is conversation-keyed; the backend resolves
          // trace_id → conversation_id via `messages.langfuse_trace_id`.
          // Null (pre-migration turn, or a turn that never stamped a
          // message) → the trace id renders as plain text, not a link that
          // lands on the wrong page.
          const inspectorPath = inspectorPathForItem(it);
          return (
            <React.Fragment key={rowId}>
              <Tr
                pending={!verdict && inFlight}
                onClick={
                  canExpand ? () => setExpandedId(expanded ? null : rowId)
                  : inspectorPath ? () => navigate(inspectorPath)
                  : undefined
                }
              >
                <Td first mono>{itemsOffset + idx + 1}</Td>
                <Td>
                  <QCell maxWidth={inFlight ? 360 : 300}>{question || "—"}</QCell>
                  {canExpand && <span style={{ color: COLORS.accentText, fontSize: 11.5, display: "block", marginTop: 2 }}>{expanded ? "▾" : "▸"} expected vs actual</span>}
                </Td>
                <Td><VerdictBadge verdict={verdict} /></Td>
                {!inFlight && (
                  <Td>{method === "—" ? <span style={{ color: COLORS.textDim }}>—</span> : <ScorerPill dim={dim}>{method}</ScorerPill>}</Td>
                )}
                <Td mono>{formatLatency(latencyOf(it))}</Td>
                <Td><TraceLink traceId={it.trace_id} onClick={inspectorPath ? () => navigate(inspectorPath) : null} /></Td>
              </Tr>
              {expanded && (
                <tr>
                  <Td colSpan={colCount} style={{ background: COLORS.surfaceHover }}>
                    <DiffRow
                      expected={expected || "(no expected answer recorded)"}
                      actual={actual != null ? actual : (
                        inspectorPath
                          ? <>No reply captured on this row — <a
                              onClick={(e) => { e.stopPropagation(); navigate(inspectorPath); }}
                              style={{ color: COLORS.accentText, cursor: "pointer", borderBottom: `1px dashed ${COLORS.accent}` }}
                            >open this turn in the Inspector</a> for the full response and its spans.</>
                          : "(no reply captured on this row, and this turn has no Inspector trace to open)"
                      )}
                      note={reasoning ? <>Judge reasoning: <B>“{reasoning}”</B> · written as a verification score on the trace, so it also shows in the Inspector.</> : null}
                    />
                  </Td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </TableCard>
  );

  return (
    <div>
      {back}
      <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 12 }}>
        {inFlight
          ? "Live — polling every 5s. You can leave this page; the run is durable and resumes on reload."
          : "Completed run — per-item verdicts below. Click any row for the expected-vs-actual diff, or open its Inspector trace."}
      </div>
      {progressBlock}
      {counters}
      {heroBlock}
      {itemsTable}
      <Pager
        offset={itemsOffset}
        limit={PAGE_SIZE}
        total={itemsTotal}
        onPrev={() => goToPage(itemsOffset - PAGE_SIZE)}
        onNext={() => goToPage(itemsOffset + PAGE_SIZE)}
      />
      {!inFlight && list.length > 0 && <Legend />}
    </div>
  );
}
